import clsx from 'clsx';
import pluralize from 'pluralize';
import React, { Dispatch, SetStateAction } from 'react';
import toast from 'react-hot-toast';

import Button from '@components/Button';
import Separator from '@components/Comments/Separator';
import IconLike from '@components/Images/Rebranding/IconLike';
import IconLink from '@components/Images/Rebranding/IconLink';
import IconPencil from '@components/Images/Rebranding/IconPencil';
import IconReply from '@components/Images/Rebranding/IconReply';
import IconReport from '@components/Images/Rebranding/IconReport';
import TimeAgo from '@components/TimeAgo';
import useCommentActions from '@hooks/useCommentActions';
import useCurrentUser from '@hooks/useCurrentUser';
import useEventTracking from '@hooks/useEventTracking';
import { edit_community_comment_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { Comment as CommentType } from '@utils/backend-api/comments';

interface CommentActionsProps {
  comment: CommentType;
  onEditComment: () => void;
  setReplyingTo: Dispatch<SetStateAction<string>>;
}

export default function CommentActions({
  comment,
  onEditComment,
  setReplyingTo,
}: CommentActionsProps) {
  const { currentUser } = useCurrentUser();
  const { report, toggleLike, liked, likesCount, areLikesLoading } = useCommentActions(comment);
  const { trackEvent } = useEventTracking();

  const handleLike = () => {
    toggleLike();
    trackEvent(click({ clickTarget: liked ? ClickTarget.CommentUnlike : ClickTarget.CommentLike }));
  };

  const handleLinkCopy = () => {
    const { origin, pathname } = window.location;

    navigator.clipboard.writeText(`${origin}${pathname}?commentId=${comment.id}`);
    trackEvent(click({ clickTarget: ClickTarget.CopyShareLink }));
    toast.success('Copied link to conversation');
  };

  const handleReply = () => {
    setReplyingTo(comment.user.username);
  };

  const handleReport = () => {
    // Don't track the event if the report was not confirmed
    report() && trackEvent(click({ clickTarget: ClickTarget.CommentReport }));
  };

  return (
    <div className="ml-0 mt-32 flex items-start gap-x-16 border-0 border-b border-solid border-grayscale-gray pb-8 sm:ml-72 sm:items-center sm:gap-x-0">
      <div className="flex flex-col items-start gap-24 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-0 sm:gap-y-8">
        <div className="flex items-center gap-16 whitespace-nowrap sm:gap-24">
          {currentUser?.permitted_to_comment && (
            <>
              <Button
                as="button"
                aria-label="reply"
                onClick={handleReply}
                className="comment-action --reply unstyled flex items-center font-suisse-screen text-byline-sm text-grayscale-gray hover:text-primary-magenta dark:hover:text-primary-pink"
              >
                <IconReply className="h-16 sm:mr-8" />
                <span className="hidden sm:inline">reply</span>
              </Button>
              <Button
                as="button"
                aria-label="like"
                onClick={handleLike}
                className="comment-action --like unstyled group flex items-center font-suisse-screen text-byline-sm text-grayscale-gray hover:text-primary-magenta dark:hover:text-primary-pink"
                disabled={areLikesLoading}
              >
                <IconLike
                  className={clsx('h-[18px] sm:mr-8', {
                    'mr-8': likesCount > 0,
                    ['text-primary-magenta']: liked,
                  })}
                />
                <>
                  {likesCount > 0 && <span className="mr-4">{likesCount}</span>}
                  <span className="hidden sm:inline-block">
                    {pluralize('like', likesCount === 0 ? 1 : likesCount)}
                  </span>
                </>
              </Button>
            </>
          )}
          <Button
            as="button"
            aria-label="copy link to this comment"
            className="unstyled flex items-center font-suisse-screen text-byline-sm text-grayscale-gray hover:text-primary-magenta dark:hover:text-primary-pink"
            onClick={handleLinkCopy}
          >
            <IconLink className="text-currentColor h-[18px] stroke-current sm:mr-4" />
            <span className="hidden sm:inline">link</span>
          </Button>
        </div>
        <div className="flex items-center whitespace-nowrap">
          <Separator className="mx-16 hidden sm:inline-block lg:mx-24" />
          {currentUser?.administrator?.is_active && !comment.promoted && (
            <>
              <Button
                as="link"
                href={edit_community_comment_path(comment.id)}
                className="comment-action --promote unstyled font-suisse-intl text-button-sm text-grayscale-gray hover:text-primary-magenta dark:hover:text-primary-pink"
              >
                promote/hide
              </Button>
              <Separator className="mx-16 lg:mx-24" />
            </>
          )}
          <TimeAgo
            className="font-suisse-intl-mono text-overline-sm text-grayscale-gray"
            date={comment.created_at}
            timeFromNow
          />
        </div>
      </div>
      <div className="ml-auto flex flex-row items-center whitespace-nowrap">
        {currentUser?.id === comment.user.id && (
          <Button
            as="button"
            aria-label="edit"
            onClick={onEditComment}
            className="unstyled mr-16 flex items-center font-suisse-intl-mono text-byline-sm text-grayscale-gray hover:text-primary-magenta dark:hover:text-primary-pink"
          >
            <IconPencil className="h-16 sm:mr-8" />
            <span className="hidden sm:inline">edit</span>
          </Button>
        )}
        <Button
          as="button"
          aria-label="report"
          onClick={handleReport}
          className="unstyled flex items-center font-suisse-screen text-byline-sm text-grayscale-gray hover:text-primary-magenta dark:hover:text-primary-pink"
        >
          <IconReport className="h-16 sm:mr-8" />
          <span className="hidden sm:inline">report</span>
        </Button>
      </div>
    </div>
  );
}
